import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Is there a newer build of this app on the server than the one running here?
 *
 * A tab left open across a deploy keeps running the bundle it loaded. Its
 * chunks are content-hashed, so after the deploy they no longer exist and the
 * first screen it has not visited fails to load, with nothing to say a reload
 * would fix it. The build writes /version.json naming its commit (scripts/
 * write-version.js), nginx serves that file uncached and never substitutes
 * index.html for it, and this hook fetches it on an interval and whenever the
 * tab becomes visible again, comparing the commit with the one baked into this
 * bundle.
 *
 * SILENT ON EVERY FAILURE. A missing file, a non-JSON body, a network error,
 * a local build with no commit: none of those is "an update". `available` only
 * becomes true on a well-formed answer naming a different commit.
 *
 * Same design as grasp-frontend-next's src/shared/updates/useUpdateCheck.ts.
 */
export const VERSION_URL = '/version.json';
export const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export const runningBuild = {
  version: process.env.REACT_APP_VERSION || '0.0.0',
  commit: (process.env.REACT_APP_BUILD_SHA || 'dev').slice(0, 7),
};

export async function fetchServedVersion(fetchImpl = fetch) {
  try {
    const response = await fetchImpl(VERSION_URL, { cache: 'no-store', credentials: 'omit' });
    if (!response.ok) return null;
    const body = await response.json();
    if (!body || typeof body !== 'object' || typeof body.commit !== 'string' || typeof body.version !== 'string') {
      return null;
    }
    return body;
  } catch {
    return null;
  }
}

export function isNewerBuild(served, running = runningBuild) {
  if (!served) return false;
  if (running.commit === 'dev') return false;
  return served.commit !== running.commit;
}

export function useUpdateCheck(intervalMs = CHECK_INTERVAL_MS) {
  const [served, setServed] = useState(null);
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const latest = await fetchServedVersion();
      if (latest) setServed(latest);
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (runningBuild.commit === 'dev') return undefined;
    // The first check is deferred: at mount the bundle IS the one just loaded.
    const timer = window.setInterval(() => {
      check();
    }, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check, intervalMs]);

  return { available: isNewerBuild(served), served, check };
}
